// Port of Location/LocationPolicy.swift and Location/BatteryPolicy.swift, plus the browser battery reading.

import { LocationTrackingModes, type LocationTrackingMode } from './locationTrackingMode';

/** What the user is doing and what the app may do, as seen by the location policy. */
export interface LocationActivity {
  /** A track recording is running (not paused). */
  isRecording: boolean;
  /** The user started navigation. */
  isNavigating: boolean;
  /** Off-route alerts are on and notifications authorised, so an alert can reach the user with the screen off. */
  screenOffAlertsAvailable: boolean;
  /** The page is visible right now. */
  isInForeground: boolean;
  /** The page has been visible at least once since it was loaded. */
  hasBeenActive: boolean;
  /** Positions are precise. */
  hasFullAccuracy: boolean;
  /** Foreground map mode chosen in Settings (battery saver, standard or precise). */
  foregroundMode: LocationTrackingMode;
}

/** The request passed to a LocationProvider (before the battery policy). */
export interface LocationPlan {
  mode: LocationTrackingMode;
  /** Background updates while the page is not visible (browsers suspend them; recorded for parity). */
  background: boolean;
  /** Compass heading (only useful while a map is on screen). */
  heading: boolean;
}

export const offLocationPlan: Readonly<LocationPlan> = { mode: 'off', background: false, heading: false };

/**
 * Decides when TrailBivvy uses location, and whether it may continue in the background.
 * - Nothing starts before the page has been visible once.
 * - Background updates only while recording, or navigating with screen-off alerts, and only with precise positions.
 * - Heading only while the page is visible.
 */
export const LocationPolicy = {
  plan(activity: LocationActivity): LocationPlan {
    if (!activity.isInForeground && !activity.hasBeenActive) return { ...offLocationPlan };
    let mode: LocationTrackingMode;
    let background: boolean;
    if (activity.isRecording) {
      mode = 'recording';
      background = activity.hasFullAccuracy;
    } else if (activity.isNavigating) {
      mode = 'navigation';
      background = activity.hasFullAccuracy && activity.screenOffAlertsAvailable;
    } else {
      mode = activity.foregroundMode;
      background = false;
    }
    if (!activity.isInForeground && !background) return { ...offLocationPlan };
    return { mode, background, heading: activity.isInForeground && LocationTrackingModes[mode].usesHeading };
  },
};

/**
 * Chooses the tracking mode to use given the requested mode and the device's power state (a hiker alone in the Alps
 * must keep enough battery for an emergency call):
 * - `off` stays `off`; charging never downgrades.
 * - Battery below 15 % and not charging → `batterySaver` for every active mode (recording is never turned off).
 * - Low Power Mode (battery not critical) downgrades navigation to standard; recording is kept.
 * - Unknown battery level (undefined, negative or non-finite) is treated as not critical.
 */
export const BatteryPolicy = {
  /** Battery fraction (0…1) below which tracking is reduced to `batterySaver`. */
  criticalBatteryLevel: 0.15,

  recommendedMode(
    requested: LocationTrackingMode,
    batteryLevel: number | undefined,
    isCharging: boolean,
    lowPowerMode: boolean,
  ): LocationTrackingMode {
    if (!LocationTrackingModes[requested].isActive) return 'off';
    if (isCharging) return requested;
    const isCritical =
      batteryLevel !== undefined && Number.isFinite(batteryLevel) && batteryLevel >= 0 && batteryLevel <= 1
        ? batteryLevel < BatteryPolicy.criticalBatteryLevel
        : false;
    if (isCritical) return 'batterySaver';
    if (lowPowerMode && requested === 'navigation') return 'standard';
    return requested;
  },
};

// MARK: Browser battery

/** Device power state as the policy sees it. */
export interface BatteryState {
  /** 0…1; undefined when the browser does not tell (Safari, Firefox). */
  level?: number;
  isCharging: boolean;
  /** No browser exposes Low Power Mode: always false. */
  lowPowerMode: boolean;
}

export const unknownBatteryState: Readonly<BatteryState> = { isCharging: false, lowPowerMode: false };

/** The part of the Battery Status API used (`navigator.getBattery()`, Chromium only). */
export interface BatteryManagerLike extends EventTarget {
  readonly level: number;
  readonly charging: boolean;
}

interface NavigatorWithBattery {
  getBattery?: () => Promise<BatteryManagerLike>;
}

function stateOf(manager: BatteryManagerLike): BatteryState {
  const state: BatteryState = { isCharging: manager.charging === true, lowPowerMode: false };
  if (Number.isFinite(manager.level)) state.level = manager.level;
  return state;
}

/** Battery state from `navigator.getBattery()` when present, else unknown (level undefined, not charging). */
export async function readBatteryState(
  nav: NavigatorWithBattery | undefined = globalThis.navigator as NavigatorWithBattery,
): Promise<BatteryState> {
  try {
    const manager = await nav?.getBattery?.();
    return manager ? stateOf(manager) : { ...unknownBatteryState };
  } catch {
    return { ...unknownBatteryState };
  }
}

/** Follows the battery (`levelchange` / `chargingchange`) where the browser exposes it. */
export class BatteryMonitor {
  private current: BatteryState = { ...unknownBatteryState };
  private manager?: BatteryManagerLike;
  private readonly listeners = new Set<(state: BatteryState) => void>();
  private readonly handle = () => {
    if (!this.manager) return;
    this.current = stateOf(this.manager);
    for (const listener of [...this.listeners]) listener(this.current);
  };

  /** Starts following the battery; resolves once the first state is known. */
  async start(nav: NavigatorWithBattery | undefined = globalThis.navigator as NavigatorWithBattery): Promise<void> {
    if (this.manager) return;
    try {
      this.manager = await nav?.getBattery?.();
    } catch {
      this.manager = undefined;
    }
    if (!this.manager) return;
    this.manager.addEventListener('levelchange', this.handle);
    this.manager.addEventListener('chargingchange', this.handle);
    this.handle();
  }

  get state(): BatteryState {
    return this.current;
  }

  subscribe(listener: (state: BatteryState) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  stop(): void {
    this.manager?.removeEventListener('levelchange', this.handle);
    this.manager?.removeEventListener('chargingchange', this.handle);
    this.manager = undefined;
  }
}
